import React, { Fragment } from 'react';

import { useSelectedVariantContext } from '../../context/TreatmentSelectorContext';
import useNextDelivery from './hooks/useNextDelivery/useNextDelivery';

import Typography from '../../../../components/_ui/_blocks/Typography/Typography';
import Icon from '../../../../components/_ui/_blocks/Icon/Icon';
import CountDownDisplay from './CountdownMessage/CountDownDisplay';

interface LoadingTextSkeletonProps {
    height: 'h-[1.125rem]' | 'h-[1.375rem]';
}

const LoadingTextSkeleton = ({ height }: LoadingTextSkeletonProps) => (
    <span className={`inline-block w-[4.75rem] skeleton-animation align-text-top ${height}`} />
)

interface Props {
    children: React.ReactNode;
    conditionName?: string
}

const ConfirmSelectionFrame = ({ children, conditionName }: Props) => {
    const selectedVariant = useSelectedVariantContext();

    const { day, cutOffTime, isLoading, error, refetch } = useNextDelivery();

    if (!selectedVariant?.type || selectedVariant.type !== 'ODONLY') {
        return (
            <div className="space-y-050">
                {children}
                {!error ? (
                    <Typography as="div" size="080" color="quiet" className="text-center !mt-050">
                        Order within{' '}
                        {!isLoading
                            ? <CountDownDisplay cutOffTime={cutOffTime} refetch={refetch} />
                            : <LoadingTextSkeleton height="h-[1.125rem]" />}
                        {' '}for delivery by{' '}
                        {!isLoading
                            ? <strong>{day}*</strong>
                            : <span className="inline-block w-[4.75rem] h-[1.125rem] skeleton-animation align-text-top" />}
                    </Typography>
                ) : null}
            </div>
        );
    }

    return (
        <div className="p-100 space-y-100 rounded-100 border border-decorators-subtle">
            <Typography as="h3" typeset="heading" size="100" className="md:text-center">
                Start your {conditionName || ''} consultation
            </Typography>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-050 md:text-center type-090">
                <li className="flex items-center gap-050 md:block">
                    <Icon icon="clipboard-light" size="medium" alt="Clipboard" className="md:mx-auto md:mb-025 w-[1rem] md:w-[1.5rem]" />
                    Complete a short consultation
                </li>
                <li className="flex items-center gap-050 md:block">
                    <Icon icon="medicine-light" size="medium" alt="Clipboard" className="md:mx-auto md:mb-025 w-[1rem] md:w-[1.5rem]" />
                    Select from appropriate treatments
                </li>
                <li className="flex items-center gap-050 md:block">
                    <Icon icon="truck-light" size="medium" alt="Clipboard" className="md:mx-auto md:mb-025 w-[1rem] md:w-[1.5rem]" />
                    {error ? (
                        'Delivered quickly and on time to your door'
                    ) : (
                        <Fragment>
                            Delivered as quickly as{' '}
                            {!isLoading
                                ? <strong>{day}*</strong>
                                : <LoadingTextSkeleton height="h-[1.375rem]" />}
                        </Fragment>
                    )}
                </li>
            </ul>
            {children}
            <Typography as="div" size="080" color="quiet" className="text-center !mt-050">
                *Subject to approval by our clinical team
            </Typography>
        </div>
    );
};

export default ConfirmSelectionFrame;
